import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { PAKETE, preisText, STEUERHINWEIS } from '@raporto/shared'
import { api } from '../net/apiClient.js'
import { ApiError } from '../net/errors.js'
import { notify } from '../ui/Toasts.js'
import { ControlShell, type Controlbereich } from '../ui/ControlShell.js'
import type {
  BetreiberListe,
  BetreiberMandant,
  BetreiberMandantZeile,
  BetreiberPaketVorschau,
  BetreiberProtokoll,
  BetreiberUebersicht,
  BetreiberWirtschaft,
  BetreiberZeitraum
} from '../net/apiClient.js'

/**
 * Paket 12 · Die Raporto Control Plane.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Was sich gegenüber dem alten Betreibercockpit ändert
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Drei Dinge, und alle drei sind Struktur und nicht Farbe:
 *
 * **Eigener Rahmen.** Die Seitenleiste zeigt nicht mehr Rechnungen, Offerten,
 * Rapporte und Kunden. Der Betreiber hat mit den Geschäftsdaten der Betriebe
 * nichts zu tun, und die Navigation soll das sagen.
 *
 * **Das Detail ist eine Seite, kein Fenster.** Das alte Detail war ein Dialog
 * von rund zweitausend Pixeln Höhe. Die Knöpfe standen unten, die
 * Fehlermeldung oben – wer unten klickte, sah die Antwort nicht. Genau daran
 * ist der erste Purge-Versuch gescheitert. Eine Seite mit Reitern hat oben
 * einen Kopf, der immer derselbe ist, und darunter jeweils so viel Inhalt,
 * wie auf einen Bildschirm passt.
 *
 * **Jede Handlung endet sichtbar.** Erfolg als Meldung, Fehler als Meldung,
 * Warten als Zustand am Knopf. Keine Handlung darf still ausgehen – das war
 * der teuerste Fehler des letzten Release.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Was gleich bleibt
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Alles, was schützt. OWNER bleibt OWNER, der Sicherheitsnachweis bleibt, das
 * Protokoll bleibt, die vier Sicherungen des Purge bleiben. Dieses Paket
 * fasst keine Route an, die etwas entscheidet – es ändert, was man sieht und
 * in welcher Reihenfolge.
 */

/* ═══════════════════════════════════════════════════════════════════════════
   Kleinkram, der mehrfach gebraucht wird
   ═══════════════════════════════════════════════════════════════════════════ */

/*
  Die Vertragszustände in Worten, die ein Betreiber liest.

  Zwei Achsen, und sie dürfen nicht vermischt werden:

    Vertragsstatus  ·  aus `tenants.lifecycle` – wo der Betrieb im
                       Vertragsleben steht
    Paket           ·  aus `plan_key` – welches Produkt gebucht ist

  Ein gebuchtes Paket macht keinen Vertragskunden. Die Registrierung vergibt
  `plan_key` an jeden, der sie durchläuft, Probebetriebe eingeschlossen.
  Deshalb stehen beide Angaben getrennt da, und aus der einen wird nie die
  andere geschlossen.

  BESTAND heisst «noch nicht klassifiziert» und nicht «kein Vertrag». Der
  Unterschied ist nicht sprachlich: «Kein Vertrag» wäre eine Aussage über den
  Kunden, und für einen produktiven Betrieb wie AV Parkett wäre sie falsch.
  «Noch nicht klassifiziert» ist eine Aussage über UNSEREN Datenstand – und
  die stimmt.
*/
const LIFECYCLE_TEXT: Record<string, string> = {
  BESTAND: 'Noch nicht klassifiziert',
  TEST: 'Testphase',
  TEST_ABGELAUFEN: 'Test abgelaufen',
  AKTIV: 'Aktiv',
  GEKUENDIGT: 'Gekündigt',
  VERTRAGSENDE: 'Vertragsende',
  GRACE_PERIOD: 'Nachfrist',
  ARCHIVIERT: 'Archiviert',
  GELOESCHT: 'Gelöscht'
}

const PAKETNAME: Record<string, string> = Object.fromEntries(PAKETE.map((p) => [p.id, p.name]))

/** Bytes als Menge, die ein Mensch liest. Dieselbe Staffel wie am Server. */
function groesse(bytes: number): string {
  if (bytes <= 0) return '0'
  const e = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.min(e.length - 1, Math.floor(Math.log(bytes) / Math.log(1000)))
  const wert = bytes / 1000 ** i
  return `${wert >= 100 || i === 0 ? Math.round(wert) : wert.toFixed(1)} ${e[i]}`
}

function datum(w: string | null | undefined): string {
  if (!w) return '—'
  const d = new Date(w)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('de-CH')
}

function oder(w: string | null | undefined, ersatz = '—'): string {
  return w && w.trim() ? w : ersatz
}

/** Der Vertragszustand als Abzeichen. BESTAND bekommt bewusst keine Warnfarbe. */
function Zustandsmarke({ wert }: { wert: string }): JSX.Element {
  const ton =
    wert === 'AKTIV'
      ? 'o-badge-ok'
      : wert === 'GEKUENDIGT' || wert === 'VERTRAGSENDE' || wert === 'GRACE_PERIOD'
        ? 'o-badge-warn'
        : 'o-badge-neutral'
  return <span className={`o-badge ${ton}`}>{LIFECYCLE_TEXT[wert] ?? wert}</span>
}

/**
 * Franken aus Rappen – Schweizer Schreibweise, ohne Rappen.
 *
 * Im Cockpit stehen Jahreswerte und Monatswerte nebeneinander; zwei
 * Nachkommastellen machen daraus eine Zahlenwand, in der niemand mehr die
 * Grössenordnung sieht. Gerundet wird kaufmännisch und nur in der Anzeige –
 * gerechnet wird überall in Rappen.
 */
function chf(rp: number): string {
  return `CHF ${Math.round(rp / 100).toLocaleString('de-CH')}`
}

/** «2026-10» als «Okt 26» – kurz genug für zwölf Beschriftungen nebeneinander. */
const MONATSKUERZEL = [
  'Jan',
  'Feb',
  'Mär',
  'Apr',
  'Mai',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Okt',
  'Nov',
  'Dez'
]
function monatKurz(m: string): string {
  const i = Number(m.slice(5, 7)) - 1
  return `${MONATSKUERZEL[i] ?? m.slice(5, 7)} ${m.slice(2, 4)}`
}

/** Ein Balken statt einer zweiten Zahl – Speicher auf einen Blick. */
function Balken({ prozent }: { prozent: number }): JSX.Element {
  const stufe = prozent >= 100 ? 'alert' : prozent >= 80 ? 'warn' : 'ok'
  return (
    <span className="cp-balken" data-stufe={stufe} aria-hidden="true">
      <i style={{ width: `${Math.min(100, Math.max(2, prozent))}%` }} />
    </span>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   Der Rahmen
   ═══════════════════════════════════════════════════════════════════════════ */

export function ControlPlane({
  benutzer,
  aufBuero,
  aufAbmelden
}: {
  benutzer: string
  aufBuero: () => void
  aufAbmelden: () => void
}): JSX.Element {
  const [bereich, setBereich] = useState<Controlbereich>('uebersicht')
  /* Welcher Betrieb offen ist – null heisst: die Liste. */
  const [offenerBetrieb, setOffenerBetrieb] = useState<string | null>(null)
  /*
    Der Filter der Betriebsliste steht hier oben und nicht in der Liste.

    Grund: Ein Punkt aus «Braucht Aufmerksamkeit» ist eine Aufgabe, und eine
    Aufgabe endet bei den Betrieben, die sie betrifft. Der Klick wechselt
    deshalb den Bereich UND setzt den Filter – läge er in der Liste, müsste
    der Betreiber nach dem Sprung von Hand nachfiltern und dabei raten,
    welcher Knopf gemeint war.
  */
  const [filter, setFilter] = useState<Filter>('alle')

  const [uebersicht, setUebersicht] = useState<BetreiberUebersicht | null>(null)
  const [liste, setListe] = useState<BetreiberListe | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  /*
    Die wirtschaftliche Lage kommt aus einer eigenen Abfrage.

    Sie liest einmal bei Stripe nach, die Übersicht misst den Speicher jedes
    Betriebs – zusammengelegt wartete die schnellere auf die langsamere. Zwei
    Zustände heissen zwei «wird geladen», und zwar jedes an seiner Stelle.
  */
  const [wirtschaft, setWirtschaft] = useState<BetreiberWirtschaft | null>(null)
  const [zeitraum, setZeitraum] = useState<BetreiberZeitraum>('monat')

  const ladeUebersicht = useCallback(async () => {
    try {
      setUebersicht(await api.betreiberUebersicht())
      setFehler(null)
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Unbekannter Fehler')
    }
  }, [])

  /*
    Ein Fehler hier darf die Übersicht nicht abräumen.

    Stripe kann langsam oder gerade nicht erreichbar sein; das ist ein
    Befund über EINEN Abschnitt und kein Grund, dem Betreiber die
    Betriebsliste wegzunehmen. Deshalb eigener Fehlerweg und eigene Meldung
    an der Kachel.
  */
  const ladeWirtschaft = useCallback(async (z: BetreiberZeitraum) => {
    try {
      setWirtschaft(await api.betreiberWirtschaft(z))
    } catch {
      setWirtschaft(null)
    }
  }, [])

  useEffect(() => {
    void ladeWirtschaft(zeitraum)
  }, [ladeWirtschaft, zeitraum])

  /*
    Was gelöscht wurde, kommt nicht zurück – auch nicht durch eine Antwort,
    die vorher losgeschickt wurde.

    Ohne diese Merkliste gab es ein Wettrennen: Eine Listenabfrage, die vor
    dem Purge begonnen hatte, traf danach ein und setzte den gelöschten
    Betrieb wieder in den Zustand. Gemessen mit der Control-Plane-Probe, die
    ihn danach wieder in der Tabelle fand.

    Ein `useRef` und kein Zustand: Die Liste beeinflusst keine Darstellung
    für sich, sie filtert nur jede Antwort, die eintrifft.
  */
  const geloescht = useRef<Set<string>>(new Set())

  const ladeListe = useCallback(async () => {
    try {
      const l = await api.betreiberMandanten(true)
      setListe({ ...l, mandanten: l.mandanten.filter((m) => !geloescht.current.has(m.id)) })
      setFehler(null)
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Unbekannter Fehler')
    }
  }, [])

  useEffect(() => {
    void ladeUebersicht()
    void ladeListe()
  }, [ladeUebersicht, ladeListe])

  /**
   * Ein Betrieb ist weg – und zwar wirklich.
   *
   * Nach dem Purge blieb bisher der alte Zustand stehen: Das Detail zeigte
   * weiter einen Betrieb, den es nicht mehr gab, und erst ein Neuladen der
   * Seite machte es eindeutig. Genau das darf bei einer Handlung, die nicht
   * umkehrbar ist, nicht passieren.
   *
   * Deshalb hier in einem Zug: aus der Liste entfernen, das Detail schliessen,
   * und beide Datenquellen neu holen. Der gelöschte Betrieb ist danach an
   * keiner Stelle des Zustands mehr vorhanden.
   */
  const betriebEntfernen = useCallback(
    (id: string) => {
      geloescht.current.add(id)
      setListe((l) =>
        l ? { ...l, mandanten: l.mandanten.filter((m) => m.id !== id) } : l
      )
      setOffenerBetrieb(null)
      void ladeListe()
      void ladeUebersicht()
    },
    [ladeListe, ladeUebersicht]
  )

  const nachAenderung = useCallback(() => {
    void ladeListe()
    void ladeUebersicht()
  }, [ladeListe, ladeUebersicht])

  /* Die Kennungen der speicherknappen Betriebe – gemessen in der Übersicht. */
  const kritisch = useMemo(
    () => new Set(uebersicht?.speicher.kritisch ?? []),
    [uebersicht]
  )

  const zahlen = {
    betriebe: liste?.mandanten.length,
    aufmerksamkeit: uebersicht?.aufmerksamkeit.length,
    automatikFehler: uebersicht?.automatik.jobs.filter((j) => j.letztesErgebnis === 'fehler').length
  }

  return (
    <ControlShell
      aktiv={bereich}
      aufWechsel={(id) => {
        setOffenerBetrieb(null)
        if (id === 'betriebe') setFilter('alle')
        setBereich(id)
      }}
      zahlen={zahlen}
      benutzer={benutzer}
      aufBuero={aufBuero}
      aufAbmelden={aufAbmelden}
      kinder={
        <>
          {fehler && (
            <p className="o-warn-text" id="cp-fehler">
              {fehler}
            </p>
          )}

          {bereich === 'uebersicht' && (
            <Uebersicht
              daten={uebersicht}
              wirtschaft={wirtschaft}
              zeitraum={zeitraum}
              setZeitraum={setZeitraum}
              aufListe={(f) => {
                setOffenerBetrieb(null)
                setFilter(f)
                setBereich('betriebe')
              }}
            />
          )}

          {bereich === 'betriebe' &&
            (offenerBetrieb ? (
              <Betriebsdetail
                id={offenerBetrieb}
                aufZurueck={() => setOffenerBetrieb(null)}
                aufGeloescht={betriebEntfernen}
                aufGeaendert={nachAenderung}
              />
            ) : (
              <Betriebsliste
                daten={liste}
                filter={filter}
                setFilter={setFilter}
                kritisch={kritisch}
                aufOeffnen={(id) => setOffenerBetrieb(id)}
                aufNeuladen={ladeListe}
              />
            ))}

          {bereich === 'pakete' && <PaketeUndNutzung daten={uebersicht} liste={liste} />}
          {bereich === 'automatik' && <Automatik daten={uebersicht} />}
          {bereich === 'protokoll' && <Protokoll />}
          {bereich === 'sicherheit' && <Sicherheit daten={uebersicht} />}
        </>
      }
    />
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   Übersicht
   ═══════════════════════════════════════════════════════════════════════════ */

function Uebersicht({
  daten,
  wirtschaft,
  zeitraum,
  setZeitraum,
  aufListe
}: {
  daten: BetreiberUebersicht | null
  wirtschaft: BetreiberWirtschaft | null
  zeitraum: BetreiberZeitraum
  setZeitraum: (z: BetreiberZeitraum) => void
  aufListe: (f: Filter) => void
}): JSX.Element {
  if (!daten) return <p className="o-soft">Wird geladen …</p>

  const b = daten.betriebe
  const aktiv = b.nachLifecycle.AKTIV ?? 0
  const gekuendigt =
    (b.nachLifecycle.GEKUENDIGT ?? 0) +
    (b.nachLifecycle.VERTRAGSENDE ?? 0) +
    (b.nachLifecycle.GRACE_PERIOD ?? 0)

  return (
    <>
      {/*
        Ganz oben die Wirtschaft, darunter der Bestand.

        Die Reihenfolge ist die Frage, die der Gründer morgens zuerst stellt:
        Wie steht Raporto da, und wo muss ich hin. Die Betriebszahlen darunter
        beantworten «wie viele sind es» – wichtig, aber nicht zuerst.
      */}
      <Wirtschaft
        daten={wirtschaft}
        zeitraum={zeitraum}
        setZeitraum={setZeitraum}
        aufListe={aufListe}
      />

      {/* ---------- Erste Ebene: die fünf Zahlen, die den Tag bestimmen ----------

          Sie sind grösser, sie stehen oben, und jede führt irgendwohin. Was
          darunter kommt, ist Zusammensetzung und Verbrauch – wichtig, aber
          nicht das, wonach man morgens zuerst schaut.
      */}
      <section className="cp-abschnitt" id="cp-kennzahlen">
        <div className="cp-abschnitt-kopf">
          <h2>Betriebe</h2>
          <p>Stand {datum(daten.heute)} · Testbetriebe sind nicht mitgezählt</p>
        </div>
        <dl className="cp-kennzahlen cp-primaer">
          <Kennzahl
            titel="Produktive Betriebe"
            wert={b.gesamt}
            zusatz={`${daten.benutzer.gesamt} Benutzer`}
            aufKlick={() => aufListe('alle')}
          />
          <Kennzahl
            titel="Aktive Verträge"
            wert={aktiv}
            zusatz="mit Vertragseinstufung"
            aufKlick={() => aufListe('aktiv')}
          />
          <Kennzahl
            titel="Gekündigt / Nachfrist"
            wert={gekuendigt}
            zusatz="Vertragsende folgt"
            aufKlick={() => aufListe('gekuendigt')}
          />
          <Kennzahl
            titel="Gesperrt"
            wert={b.gesperrt}
            zusatz="kommen an keine Route"
            ton={b.gesperrt > 0 ? 'alert' : undefined}
            aufKlick={() => aufListe('gesperrt')}
          />
          <Kennzahl
            titel="Testbetriebe"
            wert={b.testbetriebe}
            zusatz="markiert, löschbar"
            aufKlick={() => aufListe('test')}
          />
        </dl>
      </section>

      {/* ---------- Die Arbeitsliste ---------- */}
      <section className="cp-abschnitt" id="cp-aufmerksamkeit">
        <div className="cp-abschnitt-kopf">
          <h2>Braucht Aufmerksamkeit</h2>
          <p>
            {daten.aufmerksamkeit.length === 0
              ? 'nichts offen'
              : `${daten.aufmerksamkeit.length} Punkte, über die produktiven Betriebe gerechnet`}
          </p>
        </div>
        {daten.aufmerksamkeit.length === 0 ? (
          <p className="cp-ruhig">Nichts offen. Keine Grenze überschritten, keine Automatik rot.</p>
        ) : (
          <div className="cp-hinweise">
            {daten.aufmerksamkeit.map((h) =>
              h.filter ? (
                <button
                  type="button"
                  className="cp-hinweis"
                  key={h.art}
                  id={`cp-hinweis-${h.art}`}
                  data-schwere={h.schwere}
                  onClick={() => aufListe(h.filter as Filter)}
                >
                  <span className="cp-hinweis-marke" />
                  <span>{h.text}</span>
                  <span className="cp-hinweis-zahl">{h.anzahl}</span>
                  <span className="cp-hinweis-pfeil" aria-hidden="true">
                    →
                  </span>
                </button>
              ) : (
                /* Punkte ohne Liste – etwa ein Job, der zuletzt rot war. Sie
                   sehen deshalb auch nicht aus wie ein Knopf. */
                <div className="cp-hinweis" key={h.art} id={`cp-hinweis-${h.art}`} data-schwere={h.schwere}>
                  <span className="cp-hinweis-marke" />
                  <span>{h.text}</span>
                  <span className="cp-hinweis-zahl">{h.anzahl}</span>
                </div>
              )
            )}
          </div>
        )}
      </section>

      {/* ---------- Zweite Ebene: Zusammensetzung und Verbrauch ---------- */}
      <section className="cp-abschnitt cp-sekundaer">
        <div className="cp-abschnitt-kopf">
          <h2>Pakete</h2>
          <p>
            {daten.speicher.vollstaendig
              ? `Speicher über alle ${daten.speicher.gemessen} Betriebe gerechnet`
              : `Speicher über ${daten.speicher.gemessen} von ${b.gesamt} Betrieben gerechnet`}
          </p>
        </div>
        <dl className="cp-kennzahlen">
          {PAKETE.map((p) => (
            <div className="cp-kennzahl" key={p.id}>
              <dt>{p.name}</dt>
              <dd>{b.nachPaket[p.id] ?? 0}</dd>
              <span className="cp-zusatz">
                {preisText(p.preisRp)} {STEUERHINWEIS} · {p.benutzer} Benutzer · {p.speicherGb} GB
              </span>
            </div>
          ))}
          <Kennzahl
            titel="Ohne Paket"
            wert={b.nachPaket['(ohne)'] ?? 0}
            zusatz="keine Kennung hinterlegt"
            aufKlick={() => aufListe('ohnepaket')}
          />
          <Kennzahl
            titel="Speicher belegt"
            wert={groesse(daten.speicher.belegtBytes)}
            zusatz={`von ${groesse(daten.speicher.grenzeBytes)} · ${daten.speicher.prozent} %`}
          />
        </dl>
      </section>

      <section className="cp-abschnitt cp-sekundaer">
        <div className="cp-abschnitt-kopf">
          <h2>Letzte Betreiberaktionen</h2>
        </div>
        {daten.letzteAktionen.length === 0 ? (
          <p className="cp-ruhig">Noch keine Handlung protokolliert.</p>
        ) : (
          <div className="cp-tabelle">
            <table>
              <thead>
                <tr>
                  <th>Wann</th>
                  <th>Betrieb</th>
                  <th>Handlung</th>
                  <th>Betreiber</th>
                </tr>
              </thead>
              <tbody>
                {daten.letzteAktionen.map((a, i) => (
                  <tr key={i}>
                    <td data-feld="Wann">{a.wann ?? '—'}</td>
                    <td data-feld="Betrieb">{a.betrieb}</td>
                    <td data-feld="Handlung">{a.aktion}</td>
                    <td data-feld="Betreiber">{a.betreiber}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   Das Diagramm
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Zwölf Monate Geldeingang – als Säulen und nicht als Linie.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Warum Säulen
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Weil jeder Monat ein abgeschlossener Betrag ist und keine Messung auf einer
 * fortlaufenden Kurve. Eine Linie zwischen Oktober und November behauptet, es
 * habe dazwischen Zwischenwerte gegeben; die gibt es nicht. Säulen sagen
 * «dieser Monat, dieser Betrag» und nichts darüber hinaus.
 *
 * Eine Reihe, also keine Legende: Die Überschrift nennt sie. Beschriftet
 * werden nur die höchste Säule und der laufende Monat – eine Zahl über jeder
 * Säule wäre eine Tabelle in schlechter Form. Den Rest nennt der Mauszeiger,
 * und am Telefon der lange Druck.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Warum HTML und kein SVG
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Ein SVG mit fester `viewBox` skaliert seine Schrift mit: Was am Schreibtisch
 * elf Pixel hoch ist, wären am Telefon sechs. Säulen aus Elementen behalten
 * ihre Schriftgrösse, brechen bei Bedarf um und tragen die Farben aus
 * denselben Variablen wie der Rest der Oberfläche – hell wie dunkel.
 */
function Geldverlauf({
  verlauf,
  waehrung
}: {
  verlauf: Array<{ monat: string; betragRp: number; anzahl: number }>
  waehrung: string | null
}): JSX.Element {
  const hoechst = Math.max(...verlauf.map((m) => m.betragRp), 0)
  const letzter = verlauf[verlauf.length - 1]?.monat ?? ''

  if (hoechst === 0) {
    return (
      <p className="cp-ruhig" id="cp-verlauf-leer">
        In den letzten zwölf Monaten ist keine Abo-Zahlung eingegangen.
      </p>
    )
  }

  return (
    <div className="cp-diagramm" id="cp-geldverlauf">
      <div className="cp-diagramm-saeulen">
        {verlauf.map((m) => {
          /*
            Die Mindesthöhe von zwei Pixeln gilt nur für Monate MIT Geld.
            Ein leerer Monat bekommt keine Säule – ein Stummel, den man für
            einen kleinen Betrag halten kann, ist schlimmer als eine Lücke.
          */
          const anteil = m.betragRp === 0 ? 0 : Math.max(2, (m.betragRp / hoechst) * 100)
          const markiert = m.betragRp === hoechst || m.monat === letzter
          return (
            <div className="cp-saeule" key={m.monat}>
              {/*
                Die Säulenfläche ist ein eigener Kasten mit fester Höhe, und
                das ist kein Aufbau um des Aufbaus willen.

                Vorher stand der Stab als Flex-Element direkt neben
                Beschriftung und Monatsmarke. Eine Prozenthöhe rechnet dann
                gegen die Höhe des Flex-Containers, und der Rest der Zeilen
                nimmt davon weg: Gemessen wurden drei Monate mit 99'500,
                119'400 und 139'300 Rappen – und alle drei Säulen kamen
                105 Pixel hoch heraus. Ein Diagramm, in dem die obersten
                Werte gleich aussehen, ist schlimmer als keines.

                Jetzt rechnet die Prozenthöhe gegen diesen Kasten, und
                `flex-shrink: 0` am Stab verhindert, dass ihn die
                Beschriftung darüber wieder zusammendrückt.
              */}
              <span className="cp-saeule-flaeche">
                <span className="cp-saeule-wert" data-sichtbar={markiert && m.betragRp > 0}>
                  {m.betragRp > 0 ? chf(m.betragRp) : ''}
                </span>
                <span
                  className="cp-saeule-stab"
                  style={{ height: `${anteil}%` }}
                  title={`${monatKurz(m.monat)}: ${chf(m.betragRp)}${
                    waehrung ? ` ${waehrung}` : ''
                  } aus ${m.anzahl} ${m.anzahl === 1 ? 'Rechnung' : 'Rechnungen'}`}
                />
              </span>
              <span className="cp-saeule-marke">{monatKurz(m.monat)}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   Die wirtschaftliche Lage
   ═══════════════════════════════════════════════════════════════════════════ */

const ZEITRAEUME: Array<{ id: BetreiberZeitraum; label: string }> = [
  { id: 'monat', label: 'Dieser Monat' },
  { id: 'jahr', label: 'Dieses Jahr' },
  { id: 'alles', label: 'Alles' }
]

/**
 * Die sechs Zahlen, nach denen der Gründer zuerst schaut.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * MRR und Geldeingang stehen nebeneinander und werden nie addiert
 * ════════════════════════════════════════════════════════════════════════════
 *
 *   MRR          was die laufenden Abos pro Monat wert sind. Netto, nach
 *                Listenpreis, Jahresabos durch zwölf. Er ändert sich, wenn
 *                jemand bucht oder kündigt – nicht, wenn jemand zahlt.
 *   Eingegangen  was im Zeitraum wirklich bezahlt wurde. Brutto, wie es
 *                eingezogen wurde. Ein Jahresabo steht hier einmal mit zwölf
 *                Monaten und danach ein Jahr lang gar nicht.
 *
 * Deshalb trägt jede Kachel ihre Einschränkung als Unterzeile und nicht als
 * Fussnote irgendwo darunter: Wer die Zahl liest, liest auch, was sie nicht
 * weiss.
 */
function Wirtschaft({
  daten,
  zeitraum,
  setZeitraum,
  aufListe
}: {
  daten: BetreiberWirtschaft | null
  zeitraum: BetreiberZeitraum
  setZeitraum: (z: BetreiberZeitraum) => void
  aufListe: (f: Filter) => void
}): JSX.Element {
  if (!daten) {
    return (
      <section className="cp-abschnitt" id="cp-wirtschaft">
        <div className="cp-abschnitt-kopf">
          <h2>Wirtschaftliche Lage</h2>
        </div>
        <p className="o-soft">Wird geladen …</p>
      </section>
    )
  }

  const m = daten.mrr
  const g = daten.geldeingang
  const l = daten.lage
  const zr = ZEITRAEUME.find((z) => z.id === zeitraum)?.label ?? ''

  /* Der Zeitraum in Daten – damit «dieser Monat» nicht ausgelegt werden muss. */
  const spanne =
    g.vonTag && g.bisTag ? `${datum(g.vonTag)} – ${datum(g.bisTag)}` : 'ohne Einschränkung'

  return (
    <section className="cp-abschnitt" id="cp-wirtschaft">
      <div className="cp-abschnitt-kopf">
        <h2>Wirtschaftliche Lage</h2>
        <p>
          Stand {datum(daten.heute)} ·{' '}
          {daten.stripeVerbunden ? 'Abos und Zahlungen aus Stripe' : 'Stripe ist nicht eingerichtet'}
        </p>
      </div>

      {/*
        Sechs Kacheln, und deshalb eine eigene Spaltenregel.

        Die Grundregel `auto-fit, minmax(168px, 1fr)` setzt am Schreibtisch
        fünf nebeneinander – und die sechste allein in eine zweite Reihe.
        Gemessen auf 1440 Pixeln sah das aus wie eine vergessene Kachel und
        liess darunter 90 Pixel Leere stehen. Drei und drei füllen beide
        Reihen; am Telefon bleibt es bei einer Spalte.
      */}
      <dl className="cp-kennzahlen cp-primaer cp-sechs">
        <Kennzahl
          titel="MRR"
          wert={m.waehrung === null && m.betragRp === 0 ? '—' : chf(m.betragRp)}
          zusatz="Listenpreis, netto, Jahresabos durch zwölf"
          leer={m.betragRp === 0}
          aufKlick={l.zahlendAktiv > 0 ? () => aufListe('zahlend') : undefined}
        />
        <Kennzahl
          titel={`Eingegangen · ${zr}`}
          wert={g.gelesen && g.waehrung !== null ? chf(g.betragRp) : '—'}
          zusatz={g.gelesen ? `${g.anzahl} Rechnungen · ${spanne}` : 'nicht gelesen'}
          leer={!g.gelesen || g.betragRp === 0}
        />
        <Kennzahl
          titel="Zahlende Abos"
          wert={l.zahlendAktiv}
          zusatz="laufend bei Stripe"
          aufKlick={() => aufListe('zahlend')}
        />
        <Kennzahl
          titel="Testphasen"
          wert={l.testphaseLaufend}
          zusatz={
            l.testphaseAbgelaufen > 0
              ? `${l.testphaseAbgelaufen} bereits abgelaufen`
              : 'laufend, 30 Tage'
          }
          aufKlick={() => aufListe('testlaufend')}
        />
        <Kennzahl
          titel="Zahlungsprobleme"
          wert={l.zahlungsproblem}
          zusatz="Abbuchung offen"
          ton={l.zahlungsproblem > 0 ? 'alert' : undefined}
          aufKlick={l.zahlungsproblem > 0 ? () => aufListe('zahlungsproblem') : undefined}
        />
        <Kennzahl
          titel="Kündigungen"
          wert={l.gekuendigt}
          zusatz="zum Periodenende oder im Vertrag"
          ton={l.gekuendigt > 0 ? 'warn' : undefined}
          aufKlick={l.gekuendigt > 0 ? () => aufListe('gekuendigt') : undefined}
        />
      </dl>

      {/*
        Der Zeitraum steht UNTER den Kacheln und betrifft nur eine davon.
        Stünde er oben, läse er sich wie ein Filter über alles – und dann
        fragte zu Recht jemand, warum «Dieser Monat» die laufenden Abos
        wegnimmt. Ein Bestand hat keinen Zeitraum.
      */}
      <div className="cp-zeitraum" id="cp-zeitraum">
        <span className="cp-zeitraum-titel">Geldeingang:</span>
        {ZEITRAEUME.map((z) => (
          <button
            key={z.id}
            type="button"
            id={`cp-zeitraum-${z.id}`}
            className={z.id === zeitraum ? 'active' : undefined}
            onClick={() => setZeitraum(z.id)}
          >
            {z.label}
          </button>
        ))}
      </div>

      <div className="cp-abschnitt-kopf cp-diagramm-kopf">
        <h3>Geldeingang je Monat</h3>
        <p>letzte zwölf Monate · brutto, wie bei Stripe eingezogen</p>
      </div>
      {g.gelesen ? (
        <Geldverlauf verlauf={g.verlauf} waehrung={g.waehrung} />
      ) : (
        <p className="cp-ruhig" id="cp-verlauf-ungelesen">
          {g.hinweis ?? 'Der Geldeingang liess sich nicht lesen.'}
        </p>
      )}

      {/*
        Was die Zahlen NICHT wissen.

        Nicht als Beruhigung, sondern als Teil der Auskunft: Ein MRR, der drei
        Abos stillschweigend weglässt, weil ihr Preis im Stripe-Konto fehlt,
        ist genau die Zahl, die man erst entdeckt, wenn jemand nachrechnet.
      */}
      {(m.hinweis || g.hinweis || m.ohnePreis.length > 0) && (
        <div className="cp-grenzen" id="cp-wirtschaft-grenzen">
          {m.hinweis && <p>{m.hinweis}</p>}
          {g.hinweis && g.gelesen && <p>{g.hinweis}</p>}
          {m.ohnePreis.length > 0 && (
            <p id="cp-mrr-ohne-preis">
              Nicht im MRR enthalten:{' '}
              {m.ohnePreis
                .map(
                  (o) =>
                    `${o.anzahl}× ${o.paket ?? 'ohne Paket'}/${o.intervall ?? 'ohne Intervall'}`
                )
                .join(', ')}{' '}
              – dafür steht im Stripe-Konto kein Preis.
            </p>
          )}
          <p>
            Kundenindividuelle Rabatte sind nicht berücksichtigt; sie stehen weder in der
            Datenbank noch in der Preisliste. Conversion und Churn werden nicht gezeigt,
            solange die Zahlen dafür zu klein sind, um eine Quote zu tragen.
          </p>
        </div>
      )}
    </section>
  )
}

function Kennzahl({
  titel,
  wert,
  zusatz,
  leer = false,
  ton,
  aufKlick
}: {
  titel: string
  wert: number | string
  zusatz?: string
  leer?: boolean
  ton?: 'alert' | 'warn'
  aufKlick?: () => void
}): JSX.Element {
  const klassen = `cp-kennzahl${leer ? ' cp-leer' : ''}${aufKlick ? ' cp-klickbar' : ''}`
  const inhalt = (
    <>
      <dt>{titel}</dt>
      <dd>{wert}</dd>
      {zusatz && <span className="cp-zusatz">{zusatz}</span>}
    </>
  )
  if (!aufKlick) {
    return (
      <div className={klassen} data-ton={ton}>
        {inhalt}
      </div>
    )
  }
  return (
    <button type="button" className={klassen} data-ton={ton} onClick={aufKlick}>
      {inhalt}
    </button>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   Betriebsliste
   ═══════════════════════════════════════════════════════════════════════════ */

export type Filter =
  | 'alle'
  | 'aktiv'
  | 'test'
  | 'bestand'
  | 'gesperrt'
  | 'gekuendigt'
  | 'vorgemerkt'
  | 'ohnepaket'
  | 'testphase'
  | 'speicher'
  /* Die drei aus den Abo-Kennzahlen – sie kommen über eine Kachel und nicht
     über die Leiste, genau wie `vorgemerkt`, `testphase` und `speicher`. */
  | 'zahlend'
  | 'testlaufend'
  | 'zahlungsproblem'

/**
 * Die Filterleiste.
 *
 * Sieben Knöpfe stehen immer da; `vorgemerkt`, `testphase` und `speicher`
 * kommen nur dann hinzu, wenn man über die Aufmerksamkeitsliste dorthin
 * gesprungen ist. Ein Filter, der in aller Regel nichts findet, ist in der
 * Leiste nur ein Knopf, den man einmal ausprobiert und danach übersieht.
 */
const FILTER: Array<{ id: Filter; label: string }> = [
  { id: 'alle', label: 'Alle' },
  { id: 'aktiv', label: 'Aktiv' },
  { id: 'bestand', label: 'Nicht eingestuft' },
  { id: 'gekuendigt', label: 'Gekündigt' },
  { id: 'gesperrt', label: 'Gesperrt' },
  { id: 'ohnepaket', label: 'Ohne Paket' },
  { id: 'test', label: 'Testbetriebe' }
]

const ZUSATZFILTER: Record<string, string> = {
  vorgemerkt: 'Zur Löschung vorgemerkt',
  testphase: 'Testphase abgelaufen',
  speicher: 'Speicher kritisch',
  zahlend: 'Zahlende Abos',
  testlaufend: 'Testphase läuft',
  zahlungsproblem: 'Zahlung offen'
}

function passt(m: BetreiberMandantZeile, f: Filter, kritisch: Set<string>): boolean {
  switch (f) {
    case 'aktiv':
      return m.lifecycle === 'AKTIV' && !m.istTestTenant
    case 'test':
      return m.istTestTenant
    case 'bestand':
      return m.lifecycle === 'BESTAND' && !m.istTestTenant
    case 'gekuendigt':
      return ['GEKUENDIGT', 'VERTRAGSENDE', 'GRACE_PERIOD'].includes(m.lifecycle)
    case 'gesperrt':
      return m.gesperrt !== null
    case 'vorgemerkt':
      return m.loeschung !== null
    case 'ohnepaket':
      return m.paket.key === null && !m.istTestTenant
    case 'testphase':
      return m.testphase.abgelaufen && !m.istTestTenant
    case 'speicher':
      /*
        Die Liste kennt den Speicherstand nicht – er steht in Tabellen mit
        Zeilensicherheit und braucht je Betrieb eine eigene Abfrage. Die
        Übersicht misst ihn und schickt die Kennungen der knappen Betriebe
        mit; danach filtert die Liste.
      */
      return kritisch.has(m.id)
    /*
      Die drei Abo-Filter lesen `tenant_abrechnung` und nicht den Lifecycle.

      Das ist der Unterschied, auf den es ankommt: «Aktiv» oben in der Leiste
      heisst «vertraglich eingestuft», «Zahlend» heisst «bei Stripe läuft ein
      Abo». Ein Betrieb kann das eine ohne das andere sein, und genau diese
      Lücke sucht der Betreiber.
    */
    case 'zahlend':
      return m.abo.status === 'aktiv' && !m.istTestTenant
    case 'zahlungsproblem':
      return m.abo.status === 'zahlung_offen'
    case 'testlaufend':
      return m.lifecycle === 'TEST' && !m.testphase.abgelaufen && !m.istTestTenant
    default:
      return true
  }
}

function Betriebsliste({
  daten,
  filter,
  setFilter,
  kritisch,
  aufOeffnen,
  aufNeuladen
}: {
  daten: BetreiberListe | null
  filter: Filter
  setFilter: (f: Filter) => void
  kritisch: Set<string>
  aufOeffnen: (id: string) => void
  aufNeuladen: () => Promise<void>
}): JSX.Element {
  const [suche, setSuche] = useState('')

  const zeilen = useMemo(() => {
    if (!daten) return []
    const s = suche.trim().toLowerCase()
    return daten.mandanten
      .filter((m) => passt(m, filter, kritisch))
      .filter(
        (m) =>
          !s ||
          m.name.toLowerCase().includes(s) ||
          m.kuerzel.toLowerCase().includes(s) ||
          (m.adminEmail ?? '').toLowerCase().includes(s)
      )
  }, [daten, filter, suche, kritisch])

  if (!daten) return <p className="o-soft">Wird geladen …</p>

  return (
    <section className="cp-abschnitt">
      <div className="cp-werkzeuge">
        <div className="cp-suche">
          <label className="o-visuell-versteckt" htmlFor="cp-suche">
            Betrieb suchen
          </label>
          <input
            id="cp-suche"
            value={suche}
            placeholder="Betrieb, Kürzel oder Mailadresse …"
            autoComplete="off"
            onChange={(e) => setSuche(e.target.value)}
          />
        </div>
        <div className="cp-filter">
          {FILTER.map((f) => (
            <button
              key={f.id}
              id={`cp-filter-${f.id}`}
              type="button"
              className={f.id === filter ? 'active' : undefined}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
          {/* Der Filter, über den man aus der Aufmerksamkeitsliste kam. Er
              steht nur da, solange er gilt. */}
          {ZUSATZFILTER[filter] && (
            <button id={`cp-filter-${filter}`} type="button" className="active">
              {ZUSATZFILTER[filter]}
            </button>
          )}
        </div>
        <button className="o-btn o-btn-ghost" id="cp-neuladen" onClick={() => void aufNeuladen()}>
          Neu laden
        </button>
      </div>

      <p className="o-soft small" id="cp-listenzahl">
        {zeilen.length} von {daten.mandanten.length} Betrieben
      </p>

      <div className="cp-tabelle" id="cp-betriebe">
        <table>
          <thead>
            <tr>
              <th>Betrieb</th>
              <th>Zustand</th>
              <th>Paket</th>
              <th className="cp-num">Benutzer</th>
              <th>Testphase</th>
              <th>Letzte Aktivität</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {zeilen.map((m) => (
              <tr key={m.id} id={`cp-zeile-${m.kuerzel}`}>
                <td data-feld="Betrieb">
                  <span className="cp-betriebname">{m.name}</span>
                  <span className="cp-code">
                    {m.kuerzel} · Nr. {m.nummer}
                  </span>
                </td>
                <td data-feld="Zustand">
                  <span className="cp-abzeichen">
                    <Zustandsmarke wert={m.lifecycle} />
                    {m.istTestTenant && <span className="o-badge o-badge-neutral">Test</span>}
                    {m.gesperrt && <span className="o-badge o-badge-warn">gesperrt</span>}
                    {m.loeschung && <span className="o-badge o-badge-warn">vorgemerkt</span>}
                  </span>
                </td>
                <td data-feld="Paket">{oder(m.paket.name)}</td>
                <td data-feld="Benutzer" className="cp-num">
                  {m.benutzerAktiv}
                </td>
                <td data-feld="Testphase">
                  {datum(m.testphase.bis)}
                  {m.testphase.abgelaufen && (
                    <span className="o-soft small"> · abgelaufen</span>
                  )}
                </td>
                <td data-feld="Letzte Aktivität">{datum(m.letzteAktivitaet)}</td>
                <td>
                  <button
                    className="o-btn o-btn-ghost"
                    id={`cp-oeffnen-${m.kuerzel}`}
                    onClick={() => aufOeffnen(m.id)}
                  >
                    Öffnen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {zeilen.length === 0 && <p className="cp-ruhig">Kein Betrieb passt zu dieser Auswahl.</p>}
    </section>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   Betriebsdetail
   ═══════════════════════════════════════════════════════════════════════════ */

type Reiter = 'uebersicht' | 'vertrag' | 'paket' | 'benutzer' | 'aktivitaet' | 'administration'

const REITER: Array<{ id: Reiter; label: string }> = [
  { id: 'uebersicht', label: 'Übersicht' },
  { id: 'vertrag', label: 'Vertrag' },
  { id: 'paket', label: 'Paket & Nutzung' },
  { id: 'benutzer', label: 'Benutzer' },
  { id: 'aktivitaet', label: 'Aktivität' },
  { id: 'administration', label: 'Administration' }
]

function Betriebsdetail({
  id,
  aufZurueck,
  aufGeloescht,
  aufGeaendert
}: {
  id: string
  aufZurueck: () => void
  aufGeloescht: (id: string) => void
  aufGeaendert: () => void
}): JSX.Element {
  const [m, setM] = useState<BetreiberMandant | null>(null)
  const [reiter, setReiter] = useState<Reiter>('uebersicht')
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState<string | null>(null)
  const [form, setForm] = useState<
    'sperren' | 'loeschen' | 'purge' | 'sofort' | 'testmarke' | 'paket' | 'status' | null
  >(null)
  /*
    Die Checkbox der Sofortlöschung – NICHT vorausgewählt.

    Sie steht als eigener Zustand da und nicht als Teil von `bestaetigung`:
    Der abgetippte Name beweist, dass jemand den richtigen Betrieb vor sich
    hat, die Checkbox, dass er die Folgen gelesen hat. Zwei verschiedene
    Aussagen, und eine davon darf nicht die andere mitbringen.
  */
  const [folgenVerstanden, setFolgenVerstanden] = useState(false)
  const [offeneHandlung, setOffeneHandlung] = useState<(() => Promise<string>) | null>(null)
  const [code, setCode] = useState('')
  const [grund, setGrund] = useState('')
  const [bestaetigung, setBestaetigung] = useState('')
  const [zielPaket, setZielPaket] = useState('')
  const [zielStatus, setZielStatus] = useState('')
  const [vorschau, setVorschau] = useState<BetreiberPaketVorschau | null>(null)

  const laden = useCallback(async () => {
    try {
      setM(await api.betreiberMandant(id))
      setFehler(null)
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Unbekannter Fehler')
    }
  }, [id])

  useEffect(() => {
    void laden()
  }, [laden])

  /*
    Ein Formular, das aufgeht, muss auch zu sehen sein.

    Produktionsbefund: Unter «Vertrag» klappte «Vertragsstatus ändern» ein
    Formular auf, dessen Speichern-Knopf unterhalb des Bildschirmrands lag.
    Wer den Knopf nicht sieht, hält die Funktion für kaputt – und so wurde
    sie auch gemeldet.

    Das Formular steht seither weiter oben und ist kürzer. Dieser Nachsatz
    ist die Sicherung für alles andere: Die Formulare unter Paket und
    Administration stehen naturgemäss weit unten, und dort entscheidet die
    Fensterhöhe. Gescrollt wird nur, wenn es nötig ist, und nur so weit, wie
    es nötig ist – `nearest` rückt die Karte gerade eben ins Bild und reisst
    den Blick nicht vom Rest der Seite weg.
  */
  useEffect(() => {
    if (!form) return
    const karte = document.getElementById(`cp-form-${form}`)
    if (!karte) return
    const kasten = karte.getBoundingClientRect()
    if (kasten.top >= 0 && kasten.bottom <= window.innerHeight) return
    const ruhig = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
    karte.scrollIntoView({ block: 'nearest', behavior: ruhig ? 'auto' : 'smooth' })
  }, [form])

  /**
   * Eine Handlung ausführen – und sie sichtbar zu Ende bringen.
   *
   * `was` liefert den Satz, der bei Erfolg als Meldung erscheint. Der Ablauf
   * ist für jede Handlung derselbe: Knopf wartet, Ergebnis als Meldung,
   * Formular zu, Daten neu. Fehlt der Sicherheitsnachweis, wird die Handlung
   * gemerkt und nach dem Code unverändert nachgeholt.
   *
   * `schluessel` benennt den Knopf, der wartet – ohne ihn wäre entweder alles
   * gesperrt oder nichts, und beides ist falsch.
   */
  async function handeln(schluessel: string, was: () => Promise<string>): Promise<void> {
    setLaeuft(schluessel)
    setFehler(null)
    try {
      const text = await was()
      setForm(null)
      setGrund('')
      setBestaetigung('')
      setFolgenVerstanden(false)
      setZielPaket('')
      setVorschau(null)
      setOffeneHandlung(null)
      setCode('')
      await laden()
      aufGeaendert()
      notify.ok(text)
    } catch (e) {
      if (e instanceof ApiError && e.grund === 'STEP_UP_NOETIG') {
        setOffeneHandlung(() => was)
        setFehler(null)
        notify.info('Für diese Handlung ist eine Sicherheitsbestätigung nötig.')
      } else {
        const text = e instanceof Error ? e.message : 'Unbekannter Fehler'
        setFehler(text)
        notify.error(text)
      }
    } finally {
      setLaeuft(null)
    }
  }

  async function bestaetigen(): Promise<void> {
    const was = offeneHandlung
    if (!was) return
    setLaeuft('stepup')
    setFehler(null)
    try {
      await api.betreiberStepUp(code.trim())
    } catch (e) {
      const text = e instanceof Error ? e.message : 'Der Code stimmt nicht.'
      setFehler(text)
      notify.error(text)
      setLaeuft(null)
      return
    }
    setLaeuft(null)
    setCode('')
    await handeln('nachgeholt', was)
  }

  /**
   * Der Purge – die einzige Handlung, die ihren eigenen Abschluss braucht.
   *
   * Alle anderen laden den Betrieb neu. Diesen Betrieb gibt es danach nicht
   * mehr; ein Neuladen ergäbe 404 und ein Detail, das nichts mehr beschreibt.
   * Stattdessen: Meldung, Betrieb aus dem Zustand entfernen, zurück zur Liste.
   * Ohne Neuladen der Seite, und ohne dass irgendwo noch ein Rest steht.
   */
  async function purgeAusfuehren(): Promise<void> {
    if (!m) return
    setLaeuft('purge')
    setFehler(null)
    try {
      const r = await api.betreiberPurge(m.id, bestaetigung.trim(), grund.trim())
      notify.ok(`Testbetrieb «${r.geloescht}» wurde endgültig gelöscht.`)
      if (r.dateiFehler.length > 0) {
        notify.warn(
          `${r.dateiFehler.length} Datei(en) liessen sich nicht entfernen – sie stehen im Protokoll.`
        )
      }
      setForm(null)
      setGrund('')
      setBestaetigung('')
      aufGeloescht(m.id)
    } catch (e) {
      if (e instanceof ApiError && e.grund === 'STEP_UP_NOETIG') {
        setOffeneHandlung(() => async () => {
          await purgeAusfuehren()
          return ''
        })
        notify.info('Für diese Handlung ist eine Sicherheitsbestätigung nötig.')
      } else {
        const text = e instanceof Error ? e.message : 'Unbekannter Fehler'
        setFehler(text)
        notify.error(text)
      }
    } finally {
      setLaeuft(null)
    }
  }

  /**
   * Die Sofortlöschung – und warum sie NICHT wie der Purge endet.
   *
   * `purgeAusfuehren()` oben darf davon ausgehen, dass der Betrieb danach weg
   * ist: Der Betreiberpurge löscht in einer Transaktion oder wirft. Hier läuft
   * eine Kette über Stripe und den Objektspeicher, und sie kann sichtbar
   * stehenbleiben – `zustand` sagt, was wirklich passiert ist.
   *
   * Deshalb wird nur bei `erledigt` zur Liste zurückgekehrt. Bleibt der
   * Vorgang liegen, bleibt der Betrieb offen und die Meldung sagt es; wer dann
   * zur Liste geschickt würde, hielte die Löschung für erfolgt.
   */
  async function sofortLoeschen(): Promise<void> {
    if (!m) return
    setLaeuft('sofort')
    setFehler(null)
    try {
      const r = await api.betreiberSofortloeschung(
        m.id,
        bestaetigung.trim(),
        grund.trim(),
        folgenVerstanden
      )
      if (r.zustand === 'erledigt') {
        notify.ok(`${r.name}: ${r.hinweis}`)
        if (r.hinweise.length > 0) notify.warn(r.hinweise.join(' · '))
        setForm(null)
        setGrund('')
        setBestaetigung('')
        setFolgenVerstanden(false)
        aufGeloescht(m.id)
        return
      }
      /* Nicht gelöscht. Die Meldung sagt das, und der Betrieb bleibt offen. */
      notify.warn(`${r.name}: ${r.hinweis}`)
      if (r.hinweise.length > 0) notify.warn(r.hinweise.join(' · '))
      setFolgenVerstanden(false)
      await laden()
      aufGeaendert()
    } catch (e) {
      if (e instanceof ApiError && e.grund === 'STEP_UP_NOETIG') {
        setOffeneHandlung(() => async () => {
          await sofortLoeschen()
          return ''
        })
        notify.info('Für diese Handlung ist eine Sicherheitsbestätigung nötig.')
      } else {
        const text = e instanceof Error ? e.message : 'Unbekannter Fehler'
        setFehler(text)
        notify.error(text)
      }
    } finally {
      setLaeuft(null)
    }
  }

  if (!m) {
    return (
      <>
        <button className="o-btn o-btn-ghost" onClick={aufZurueck}>
          ← Alle Betriebe
        </button>
        {fehler ? <p className="o-warn-text">{fehler}</p> : <p className="o-soft">Wird geladen …</p>}
      </>
    )
  }

  const wartet = (k: string): boolean => laeuft === k
  const irgendwas = laeuft !== null

  return (
    <>
      <button className="o-btn o-btn-ghost" id="cp-zurueck" onClick={aufZurueck}>
        ← Alle Betriebe
      </button>

      {/* ---------- Kopf ---------- */}
      <div className="cp-detailkopf">
        <div>
          <h2 id="cp-detail-name">{m.name}</h2>
          <div className="cp-abzeichen">
            <span className="cp-code">
              {m.kuerzel} · Nr. {m.nummer}
            </span>
            <Zustandsmarke wert={m.vertrag.lifecycle} />
            {m.vertrag.istTestTenant && (
              <span className="o-badge o-badge-neutral" id="cp-badge-test">
                Testbetrieb
              </span>
            )}
            {m.paket.name && <span className="o-badge o-badge-neutral">{m.paket.name}</span>}
            {m.gesperrt && <span className="o-badge o-badge-warn">gesperrt</span>}
            {m.loeschung && <span className="o-badge o-badge-warn">Löschung vorgemerkt</span>}
          </div>
        </div>
        {/*
          Die primären Handlungen, und nur die. Alles Weitere liegt im
          jeweiligen Reiter, das Unumkehrbare unter Administration. Fünf
          gleichwertige Knöpfe nebeneinander zwingen jedes Mal zum Lesen
          aller fünf.
        */}
        <div className="cp-detail-aktionen">
          <button
            className="o-btn o-btn-primary"
            id="cp-btn-paket"
            disabled={irgendwas}
            onClick={() => {
              setVorschau(null)
              setZielPaket('')
              setReiter('paket')
              setForm(form === 'paket' ? null : 'paket')
            }}
          >
            Paket wechseln
          </button>
          {m.gesperrt ? (
            <button
              className="o-btn o-btn-ghost"
              id="cp-btn-entsperren"
              disabled={irgendwas}
              onClick={() =>
                void handeln('entsperren', async () => {
                  const r = await api.betreiberEntsperren(m.id)
                  return `${m.name}: ${r.hinweis}`
                })
              }
            >
              {wartet('entsperren') ? 'Wird entsperrt …' : 'Sperre aufheben'}
            </button>
          ) : (
            <button
              className="o-btn o-btn-ghost"
              id="cp-btn-sperren"
              disabled={irgendwas}
              onClick={() => {
                setReiter('vertrag')
                setForm(form === 'sperren' ? null : 'sperren')
              }}
            >
              Betrieb sperren
            </button>
          )}
        </div>
      </div>

      {/* ---------- Nachweis und Fehler, unmittelbar unter dem Kopf ---------- */}
      {offeneHandlung && (
        <div className="o-verschlossen" id="cp-stepup">
          <p className="o-verschlossen-satz">
            <strong>Sicherheitsbestätigung nötig</strong>
          </p>
          <p className="o-soft small">
            Diese Handlung greift in einen fremden Betrieb ein. Bitte den aktuellen Code aus der
            Authenticator-App eingeben. Danach läuft die Handlung weiter.
          </p>
          <div className="o-field" style={{ maxWidth: 220 }}>
            <label htmlFor="cp-stepup-code">Code</label>
            <input
              id="cp-stepup-code"
              value={code}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={20}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && code.trim().length >= 6) void bestaetigen()
              }}
            />
          </div>
          <div className="o-aktionen">
            <button
              className="o-btn o-btn-primary"
              id="cp-btn-stepup"
              disabled={irgendwas || code.trim().length < 6}
              onClick={() => void bestaetigen()}
            >
              {wartet('stepup') ? 'Wird geprüft …' : 'Bestätigen und fortfahren'}
            </button>
            <button
              className="o-btn o-btn-ghost"
              id="cp-btn-stepup-abbrechen"
              disabled={irgendwas}
              onClick={() => {
                setOffeneHandlung(null)
                setCode('')
              }}
            >
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {fehler && (
        <p className="o-warn-text" id="cp-detail-fehler">
          {fehler}
        </p>
      )}

      {/* ---------- Reiter ---------- */}
      <div className="cp-reiter" role="tablist">
        {REITER.map((r) => (
          <button
            key={r.id}
            id={`cp-reiter-${r.id}`}
            type="button"
            role="tab"
            aria-selected={r.id === reiter}
            className={r.id === reiter ? 'active' : undefined}
            onClick={() => setReiter(r.id)}
          >
            {r.label}
          </button>
        ))}
      </div>

      {reiter === 'uebersicht' && (
        <section className="cp-abschnitt" id="cp-tab-uebersicht">
          <dl className="cp-werte">
            <div>
              <dt>Paket</dt>
              <dd>
                {oder(m.paket.name)}
                {m.paket.listenpreisRp !== null && (
                  <span className="o-soft small"> · {preisText(m.paket.listenpreisRp)} {STEUERHINWEIS}</span>
                )}
              </dd>
            </div>
            <div>
              <dt>Benutzer</dt>
              <dd>
                {/*
                  «0 von 5 Benutzern», darunter klein «Pro · Paketlimit 5».

                  Vorher stand da «0 / 5 Paketlimit: 5 Benutzer» – dieselbe
                  Aussage zweimal, einmal als Bruch und einmal als Satz. Die
                  Zahl gehört nach oben, die Herkunft der Grenze nach unten.
                */}
                {m.paket.benutzerGrenze === null
                  ? `${m.paket.benutzer} Benutzer`
                  : `${m.paket.benutzer} von ${m.paket.benutzerGrenze} Benutzern`}
                {m.paket.ueberGrenze && (
                  <span className="o-badge o-badge-warn"> über dem Limit</span>
                )}
                <span className="cp-unterzeile">
                  {m.paket.benutzerGrenze === null
                    ? 'kein Paket hinterlegt'
                    : `${m.paket.name} · Paketlimit ${m.paket.benutzerGrenze}`}
                </span>
              </dd>
            </div>
            <div>
              <dt>Speicher</dt>
              <dd>
                {groesse(m.speicher.belegtBytes)} von {m.speicher.gbGrenze} GB
                <Balken prozent={m.speicher.prozent} />
                <span className="cp-unterzeile">{m.speicher.prozent} % belegt</span>
              </dd>
            </div>
            <div>
              <dt>Vertragszustand</dt>
              <dd>
                <Zustandsmarke wert={m.vertrag.lifecycle} />
              </dd>
            </div>
            <div>
              <dt>Testphase bis</dt>
              <dd>
                {datum(m.testphase.bis)}
                {m.testphase.abgelaufen && <span className="o-soft small"> · abgelaufen</span>}
              </dd>
            </div>
            <div>
              <dt>Angelegt</dt>
              <dd>{datum(m.erstelltAm)}</dd>
            </div>
          </dl>
          <p className="cp-schutzhinweis">
            Endkundendaten dieses Betriebs sind hier nicht einsehbar – nur Anzahl und
            Nutzungszahlen.
          </p>
        </section>
      )}

      {reiter === 'vertrag' && (
        <section className="cp-abschnitt" id="cp-tab-vertrag">
          {/*
            Zwei Zeilen, zwei Achsen – und genau deshalb stehen sie
            nebeneinander und nicht in einer.

            «Paket: Pro» sagt, was gebucht ist. «Vertragsstatus: Aktiv» sagt,
            ob daraus ein laufender Vertrag geworden ist. In einer Zeile
            zusammengefasst wäre jeder Probebetrieb mit Paketkennung ein
            Vertragskunde – und die Automatik der Pakete 14/16 fasst genau die
            dann an.
          */}
          {/*
            Zustand und Handlung stehen zuoberst – und zwar zusammen.

            Produktionsbefund: Vorher war «Vertragsstatus» der erste von
            sieben Werten in der Tabelle, der Knopf stand darunter und das
            Formular noch weiter unten. Bei einem Fenster von 1624x930 lag
            der Speichern-Knopf damit knapp unterhalb des Bildschirmrands:
            Die Auswahl war zu sehen, der Grund halb, die Bestätigung gar
            nicht. Gemessen 856 Pixel ab Seitenanfang.

            Der Knopf gehört neben den Zustand, den er ändert, und das
            Formular direkt darunter. Die übrigen Vertragswerte sind zum
            Nachlesen da und stehen deshalb hinter der Handlung.

            Und sie steht hier und nicht unter Administration: Einen Betrieb
            als produktiven Kunden einzustufen ist eine gewöhnliche
            kaufmännische Handlung und nichts Unumkehrbares. Unter
            Administration stünde sie neben dem endgültigen Löschen, und eine
            Alltagshandlung im rot umrandeten Kasten lehrt nur eines: dass der
            rote Kasten nichts zu bedeuten hat.

            Die Auswahl kommt aus `moeglicheZiele` vom Server. Was dort nicht
            steht, gibt es hier nicht – der Übergang AKTIV → ARCHIVIERT etwa
            führt über GEKUENDIGT, und das entscheidet die Übergangstabelle im
            Dienst, nicht dieser Bildschirm.
          */}
          <div className="cp-vertragskopf">
            <div>
              <span className="cp-vertragskopf-titel">Vertragsstatus</span>
              <span className="cp-vertragskopf-wert" id="cp-vertrag-status">
                <Zustandsmarke wert={m.vertrag.lifecycle} />
                {m.vertrag.istTestTenant && (
                  <>
                    {' '}
                    <span className="o-badge o-badge-neutral">Testbetrieb</span>
                  </>
                )}
              </span>
            </div>
            {m.vertrag.moeglicheZiele.length > 0 && (
              <button
                className="o-btn o-btn-ghost"
                id="cp-btn-status"
                disabled={irgendwas}
                onClick={() => {
                  setZielStatus('')
                  setGrund('')
                  setForm(form === 'status' ? null : 'status')
                }}
              >
                Vertragsstatus ändern
              </button>
            )}
          </div>

          {form === 'status' && (
            <div className="cp-karte" id="cp-form-status">
              <div className="cp-formzeile">
                <div className="cp-formfeld">
                  <label htmlFor="cp-ziel-status">Neuer Vertragsstatus</label>
                  <select
                    id="cp-ziel-status"
                    value={zielStatus}
                    onChange={(e) => setZielStatus(e.target.value)}
                  >
                    <option value="">— wählen —</option>
                    {m.vertrag.moeglicheZiele.map((z) => (
                      <option key={z} value={z}>
                        {LIFECYCLE_TEXT[z] ?? z}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="cp-formfeld">
                  <label htmlFor="cp-grund-status">
                    Grund — er steht im Vertragsverlauf und bleibt dort stehen
                  </label>
                  <input
                    id="cp-grund-status"
                    value={grund}
                    onChange={(e) => setGrund(e.target.value)}
                    placeholder="z. B. produktiver Kunde seit Übernahme aus AV Parkett Office"
                    autoComplete="off"
                  />
                </div>
              </div>
              <div className="o-dialog-fuss">
                <button className="o-btn o-btn-ghost" onClick={() => setForm(null)}>
                  Abbrechen
                </button>
                {/*
                  Der Hinweis steht NEBEN dem Knopf und nicht unter dem Feld.

                  Produktionsbefund: Nach der Auswahl von «Aktiv» bleibt der
                  Knopf grau, weil die Begründung fehlt – und wer auf einen
                  grauen Knopf klickt, hält ihn für kaputt. Der Satz stand
                  schon da, aber weiter oben, wo man ihn im Moment des Klicks
                  nicht liest.
                */}
                {grund.trim().length < 3 && (
                  <span className="o-soft small" id="cp-status-fehlt" data-grundhinweis>
                    Bitte mindestens 3 Zeichen als Begründung eingeben.
                  </span>
                )}
                <button
                  className="o-btn o-btn-primary"
                  id="cp-btn-status-bestaetigen"
                  disabled={irgendwas || zielStatus === '' || grund.trim().length < 3}
                  onClick={() =>
                    void handeln('status', async () => {
                      const r = await api.betreiberVertragsstatus(m.id, zielStatus, grund.trim())
                      return r.geaendert
                        ? `${m.name}: Vertragsstatus ${LIFECYCLE_TEXT[r.von] ?? r.von} → ` +
                            `${LIFECYCLE_TEXT[r.nach] ?? r.nach}.`
                        : `${m.name}: stand bereits auf ${LIFECYCLE_TEXT[r.nach] ?? r.nach}.`
                    })
                  }
                >
                  {wartet('status') ? 'Wird gesetzt …' : 'Vertragsstatus setzen'}
                </button>
              </div>
            </div>
          )}

          <dl className="cp-werte">
            <div>
              <dt>Paket</dt>
              <dd id="cp-vertrag-paket">{m.paket.name ?? 'keines hinterlegt'}</dd>
            </div>
            <div>
              <dt>Vertragsbeginn</dt>
              <dd>{datum(m.vertrag.beginn)}</dd>
            </div>
            <div>
              <dt>Gekündigt am</dt>
              <dd>{m.vertrag.gekuendigtAm ?? '—'}</dd>
            </div>
            <div>
              <dt>Wirksam per</dt>
              <dd>{datum(m.vertrag.wirksamPer)}</dd>
            </div>
            <div>
              <dt>Nachfrist bis</dt>
              <dd>{datum(m.vertrag.graceBis)}</dd>
            </div>
            <div>
              <dt>Sperre</dt>
              <dd>{m.gesperrt ? `seit ${m.gesperrt.seit} — ${m.gesperrt.grund}` : 'keine'}</dd>
            </div>
          </dl>
          {m.vertrag.hinweis && <p className="o-soft small">{m.vertrag.hinweis}</p>}

          {form === 'sperren' && (
            <div className="cp-karte" id="cp-form-sperren">
              <label htmlFor="cp-grund-sperren">
                Grund — er steht im Protokoll und ist das, was beim Anruf auf dem Schirm steht
              </label>
              <input
                id="cp-grund-sperren"
                value={grund}
                onChange={(e) => setGrund(e.target.value)}
                placeholder="z. B. Zahlungsrückstand nach dreimaliger Mahnung"
                autoComplete="off"
              />
              <Grundhinweis wert={grund} />
              <div className="o-dialog-fuss">
                <button className="o-btn o-btn-ghost" onClick={() => setForm(null)}>
                  Abbrechen
                </button>
                <button
                  className="o-btn o-btn-primary"
                  id="cp-btn-sperren-bestaetigen"
                  disabled={irgendwas || grund.trim().length < 3}
                  onClick={() =>
                    void handeln('sperren', async () => {
                      const r = await api.betreiberSperren(m.id, grund.trim())
                      return `${m.name} gesperrt. ${r.beendeteSitzungen} Sitzungen beendet.`
                    })
                  }
                >
                  {wartet('sperren') ? 'Wird gesperrt …' : 'Jetzt sperren'}
                </button>
              </div>
            </div>
          )}

          {m.vertrag.verlauf.length > 0 && (
            <div className="cp-tabelle">
              <table>
                <thead>
                  <tr>
                    <th>Wann</th>
                    <th>Wechsel</th>
                    <th>Grund</th>
                  </tr>
                </thead>
                <tbody>
                  {m.vertrag.verlauf.map((e, i) => (
                    <tr key={i}>
                      <td data-feld="Wann">{datum(e.wann)}</td>
                      <td data-feld="Wechsel">
                        {(e.von ? (LIFECYCLE_TEXT[e.von] ?? e.von) : 'entstanden') +
                          ' → ' +
                          (LIFECYCLE_TEXT[e.nach] ?? e.nach)}
                      </td>
                      <td data-feld="Grund">{e.grund || '–'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {reiter === 'paket' && (
        <section className="cp-abschnitt" id="cp-tab-paket">
          <dl className="cp-werte">
            <div>
              <dt>Gebuchtes Paket</dt>
              <dd>{oder(m.paket.name)}</dd>
            </div>
            <div>
              <dt>Benutzer</dt>
              <dd>
                {m.paket.benutzerGrenze === null
                  ? `${m.paket.benutzer} Benutzer`
                  : `${m.paket.benutzer} von ${m.paket.benutzerGrenze} Benutzern`}
              </dd>
            </div>
            <div>
              <dt>Speicher</dt>
              <dd>
                {groesse(m.speicher.belegtBytes)} von {m.speicher.gbGrenze} GB
                <Balken prozent={m.speicher.prozent} />
                <span className="cp-unterzeile">{m.speicher.prozent} % belegt</span>
              </dd>
            </div>
            <div>
              <dt>Davon</dt>
              <dd className="o-soft small">
                Fotos {groesse(m.speicher.fotosBytes)} · Belege {groesse(m.speicher.belegeBytes)} ·
                Logo {groesse(m.speicher.logoBytes)}
              </dd>
            </div>
          </dl>

          {form === 'paket' && (
            <div className="cp-karte" id="cp-form-paket">
              <p className="o-soft small">
                Der Wechsel ändert die <strong>Paketgrenzen</strong> dieses Betriebs und gilt ab
                sofort. Verrechnet, gutgeschrieben oder eingezogen wird nichts – die Abrechnung
                läuft ausserhalb von Raporto.
              </p>
              <label htmlFor="cp-ziel-paket">Neues Paket</label>
              <select
                id="cp-ziel-paket"
                value={zielPaket}
                onChange={(e) => {
                  const wert = e.target.value
                  setZielPaket(wert)
                  setVorschau(null)
                  if (!wert) return
                  void api
                    .betreiberPaketVorschau(m.id, wert)
                    .then(setVorschau)
                    .catch((err: unknown) => {
                      const t = err instanceof Error ? err.message : 'Unbekannter Fehler'
                      setFehler(t)
                      notify.error(t)
                    })
                }}
              >
                <option value="">— bitte wählen —</option>
                {PAKETE.map((p) => (
                  <option key={p.id} value={p.id} disabled={p.id === m.paket.key}>
                    {p.name} · {preisText(p.preisRp)} {STEUERHINWEIS} · {p.benutzer} Benutzer ·{' '}
                    {p.speicherGb} GB{p.id === m.paket.key ? ' (aktuell)' : ''}
                  </option>
                ))}
              </select>

              {vorschau && (
                <div id="cp-paket-vorschau">
                  <p className="o-soft small">
                    {vorschau.vonName ?? 'kein Paket'} → {vorschau.nachName} ·{' '}
                    {vorschau.richtung === 'hoch'
                      ? 'Hochstufung'
                      : vorschau.richtung === 'runter'
                        ? 'Herabstufung'
                        : 'Wechsel'}
                    {' · '}Benutzer {vorschau.benutzer} / {vorschau.benutzerGrenzeNeu} · Speicher{' '}
                    {groesse(vorschau.belegtBytes)} / {groesse(vorschau.grenzeBytesNeu)}
                  </p>
                  {vorschau.hindernisse.map((h) => (
                    <p className="o-warn-text" key={h.art}>
                      {h.text}
                    </p>
                  ))}
                </div>
              )}

              <label htmlFor="cp-grund-paket">Grund</label>
              <input
                id="cp-grund-paket"
                value={grund}
                onChange={(e) => setGrund(e.target.value)}
                placeholder="z. B. Kunde hat auf Pro erweitert, Bestellung vom 12.09."
                autoComplete="off"
              />
              <Grundhinweis wert={grund} />
              <div className="o-dialog-fuss">
                <button className="o-btn o-btn-ghost" onClick={() => setForm(null)}>
                  Abbrechen
                </button>
                <button
                  className="o-btn o-btn-primary"
                  id="cp-btn-paket-bestaetigen"
                  disabled={
                    irgendwas ||
                    grund.trim().length < 3 ||
                    !zielPaket ||
                    vorschau === null ||
                    !vorschau.moeglich
                  }
                  onClick={() =>
                    void handeln('paket', async () => {
                      const r = await api.betreiberPaketWechseln(m.id, zielPaket, grund.trim())
                      return `${m.name}: ${r.hinweis}`
                    })
                  }
                >
                  {wartet('paket') ? 'Wird gewechselt …' : 'Paket wechseln'}
                </button>
              </div>
            </div>
          )}

          {m.paket.verlauf.length > 0 && (
            <div className="cp-tabelle">
              <table>
                <thead>
                  <tr>
                    <th>Wann</th>
                    <th>Paketwechsel</th>
                    <th>Grund</th>
                  </tr>
                </thead>
                <tbody>
                  {m.paket.verlauf.map((e, i) => (
                    <tr key={i}>
                      <td data-feld="Wann">{datum(e.wann)}</td>
                      <td data-feld="Paketwechsel">
                        {(e.von ? (PAKETNAME[e.von] ?? e.von) : 'gewählt') +
                          ' → ' +
                          (e.nach ? (PAKETNAME[e.nach] ?? e.nach) : 'kein Paket')}
                      </td>
                      <td data-feld="Grund">{e.grund || '–'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {reiter === 'benutzer' && (
        <section className="cp-abschnitt" id="cp-tab-benutzer">
          <div className="cp-abschnitt-kopf">
            <h2>Zugänge</h2>
            <button
              className="o-btn o-btn-ghost"
              id="cp-btn-zugangslink"
              disabled={irgendwas || !m.admin}
              onClick={() =>
                void handeln('zugangslink', async () => {
                  const r = await api.betreiberZugangslink(m.id)
                  return r.versandFehler
                    ? `Link erzeugt, aber ${r.versandFehler} (an ${r.an})`
                    : `Passwort-Link an ${r.an} verschickt.`
                })
              }
            >
              {wartet('zugangslink') ? 'Wird verschickt …' : 'Passwort-Link an Admin'}
            </button>
          </div>
          <div className="cp-tabelle">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>E-Mail</th>
                  <th>Rolle</th>
                  <th>Zweiter Faktor</th>
                  <th>Zustand</th>
                </tr>
              </thead>
              <tbody>
                {m.team.map((t, i) => (
                  <tr key={i}>
                    <td data-feld="Name">{oder(t.name)}</td>
                    <td data-feld="E-Mail">{oder(t.email)}</td>
                    <td data-feld="Rolle">{t.rolle}</td>
                    <td data-feld="Zweiter Faktor">{t.zweiterFaktor ? 'ja' : 'nein'}</td>
                    <td data-feld="Zustand">
                      {t.aktiv ? (
                        <span className="o-badge o-badge-ok">aktiv</span>
                      ) : (
                        <span className="o-badge o-badge-neutral">deaktiviert</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {reiter === 'aktivitaet' && (
        <section className="cp-abschnitt" id="cp-tab-aktivitaet">
          <dl className="cp-kennzahlen">
            <Kennzahl titel="Kunden" wert={m.kunden} />
            <Kennzahl titel="Projekte" wert={m.projekte} />
            <Kennzahl titel="Rechnungen" wert={m.rechnungen} zusatz={`${m.rechnungenMonat} diesen Monat`} />
            <Kennzahl titel="Offerten" wert={m.offerten} zusatz={`${m.offertenMonat} diesen Monat`} />
            <Kennzahl titel="Rapporte" wert={m.rapporte} zusatz={`${m.rapporteMonat} diesen Monat`} />
          </dl>
          <p className="cp-schutzhinweis">
            Zahlen, keine Namen. Die Endkundendaten des Betriebs verlassen den Mandanten nicht.
          </p>
        </section>
      )}

      {reiter === 'administration' && (
        <section className="cp-abschnitt" id="cp-tab-administration">
          <div className="cp-administration" id="cp-administration">
            <h3>Administration</h3>
            <p>
              Handlungen, die Daten entfernen oder den Betrieb für seine Leute unbrauchbar machen.
              Jede verlangt eine Begründung und einen frischen Sicherheitsnachweis, jede steht im
              Protokoll.
            </p>

            <div className="cp-administration-reihe">
              {m.loeschung ? (
                <button
                  className="o-btn o-btn-ghost"
                  id="cp-btn-loeschung-zurueck"
                  disabled={irgendwas}
                  onClick={() =>
                    void handeln('vormerkung', async () => {
                      await api.betreiberLoeschungZuruecknehmen(m.id)
                      return `${m.name}: Löschvormerkung zurückgenommen.`
                    })
                  }
                >
                  {wartet('vormerkung') ? 'Wird zurückgenommen …' : 'Vormerkung zurücknehmen'}
                </button>
              ) : (
                <button
                  className="o-btn o-btn-ghost"
                  id="cp-btn-loeschen"
                  disabled={irgendwas}
                  onClick={() => setForm(form === 'loeschen' ? null : 'loeschen')}
                >
                  Löschung vormerken
                </button>
              )}

              {!m.vertrag.istTestTenant &&
                !['AKTIV', 'GEKUENDIGT', 'VERTRAGSENDE', 'GRACE_PERIOD'].includes(
                  m.vertrag.lifecycle
                ) && (
                  <button
                    className="o-btn o-btn-ghost"
                    id="cp-btn-testmarke"
                    disabled={irgendwas}
                    onClick={() => setForm(form === 'testmarke' ? null : 'testmarke')}
                  >
                    Als Testbetrieb markieren <span className="cp-owner">Owner</span>
                  </button>
                )}

              {m.vertrag.istTestTenant && (
                <>
                  <button
                    className="o-btn o-btn-ghost"
                    id="cp-btn-testmarke-weg"
                    disabled={irgendwas}
                    onClick={() =>
                      void handeln('testmarke-weg', async () => {
                        await api.betreiberTestmarkierung(m.id, false, 'kein Testbetrieb mehr')
                        return `${m.name}: Markierung als Testbetrieb entfernt.`
                      })
                    }
                  >
                    {wartet('testmarke-weg') ? 'Wird entfernt …' : 'Markierung entfernen'}
                  </button>
                  <button
                    className="o-btn o-btn-alert"
                    id="cp-btn-purge"
                    disabled={irgendwas}
                    onClick={() => setForm(form === 'purge' ? null : 'purge')}
                  >
                    Testbetrieb endgültig löschen <span className="cp-owner">Owner</span>
                  </button>
                </>
              )}

              {/*
                Die Sofortlöschung eines KUNDENBETRIEBS – und sie steht nur bei
                einem solchen da.

                Für einen Testbetrieb gibt es den Knopf darüber; zwei Wege für
                denselben Betrieb nebeneinander wären die Einladung, den
                falschen zu nehmen. Der Plattformbetrieb hat keinen von beiden.
              */}
              {!m.vertrag.istTestTenant && (
                <button
                  className="o-btn o-btn-alert"
                  id="cp-btn-sofort"
                  disabled={irgendwas}
                  onClick={() => {
                    setFolgenVerstanden(false)
                    setForm(form === 'sofort' ? null : 'sofort')
                  }}
                >
                  Sofortlöschung auf Kundenwunsch <span className="cp-owner">Owner</span>
                </button>
              )}
            </div>

            {form === 'testmarke' && (
              <div className="cp-karte" id="cp-form-testmarke">
                <p className="o-soft small">
                  Ein Testbetrieb lässt sich danach <strong>endgültig löschen</strong>. Diese
                  Markierung ist deshalb selbst eine Entscheidung und steht im Protokoll.
                </p>
                <label htmlFor="cp-grund-testmarke">Grund</label>
                <input
                  id="cp-grund-testmarke"
                  value={grund}
                  onChange={(e) => setGrund(e.target.value)}
                  placeholder="z. B. Demobetrieb für die Messe, nur Testdaten"
                  autoComplete="off"
                />
                <Grundhinweis wert={grund} />
                <div className="o-dialog-fuss">
                  <button className="o-btn o-btn-ghost" onClick={() => setForm(null)}>
                    Abbrechen
                  </button>
                  <button
                    className="o-btn"
                    id="cp-btn-testmarke-bestaetigen"
                    disabled={irgendwas || grund.trim().length < 3}
                    onClick={() =>
                      void handeln('testmarke', async () => {
                        await api.betreiberTestmarkierung(m.id, true, grund.trim())
                        return `${m.name}: als Testbetrieb markiert.`
                      })
                    }
                  >
                    {wartet('testmarke') ? 'Wird markiert …' : 'Als Testbetrieb markieren'}
                  </button>
                </div>
              </div>
            )}

            {form === 'loeschen' && (
              <div className="cp-karte" id="cp-form-loeschen">
                <p className="o-soft small">
                  Die Vormerkung löscht nichts. Sie hält fest, dass gelöscht werden soll – und ist
                  jederzeit zurücknehmbar.
                </p>
                <label htmlFor="cp-grund-loeschen">Grund</label>
                <input
                  id="cp-grund-loeschen"
                  value={grund}
                  onChange={(e) => setGrund(e.target.value)}
                  placeholder="z. B. Kündigung per Ende Jahr, Daten nach Frist entfernen"
                  autoComplete="off"
                />
                <Grundhinweis wert={grund} />
                <label htmlFor="cp-bestaetigung-loeschen">
                  Zur Bestätigung den Namen eintippen: <strong>{m.name}</strong>
                </label>
                <input
                  id="cp-bestaetigung-loeschen"
                  value={bestaetigung}
                  onChange={(e) => setBestaetigung(e.target.value)}
                  autoComplete="off"
                />
                <div className="o-dialog-fuss">
                  <button className="o-btn o-btn-ghost" onClick={() => setForm(null)}>
                    Abbrechen
                  </button>
                  <button
                    className="o-btn"
                    id="cp-btn-loeschen-bestaetigen"
                    disabled={
                      irgendwas || grund.trim().length < 3 || bestaetigung.trim() !== m.name.trim()
                    }
                    onClick={() =>
                      void handeln('loeschen', async () => {
                        const r = await api.betreiberLoeschungVormerken(
                          m.id,
                          grund.trim(),
                          bestaetigung.trim()
                        )
                        return `${m.name}: ${r.hinweis}`
                      })
                    }
                  >
                    {wartet('loeschen') ? 'Wird vorgemerkt …' : 'Löschung vormerken'}
                  </button>
                </div>
              </div>
            )}

            {form === 'purge' && (
              <div className="cp-karte" id="cp-form-purge">
                <p className="o-warn-text">
                  <strong>Das ist endgültig.</strong> Alle Daten dieses Testbetriebs werden
                  entfernt: Kunden, Projekte, Rapporte, Fotos, Belege, Zugänge und Dateien. Es gibt
                  keine Wiederherstellung und keinen Papierkorb.
                </p>
                <p className="o-soft small">
                  Betroffen: {m.kunden} Kunden · {m.projekte} Projekte · {m.rapporte} Rapporte ·{' '}
                  {m.rechnungen + m.offerten} Belege · {groesse(m.speicher.belegtBytes)} Dateien
                </p>
                <label htmlFor="cp-grund-purge">Grund</label>
                <input
                  id="cp-grund-purge"
                  value={grund}
                  onChange={(e) => setGrund(e.target.value)}
                  placeholder="z. B. Feldtest abgeschlossen, Demodaten entfernen"
                  autoComplete="off"
                />
                <Grundhinweis wert={grund} />
                <label htmlFor="cp-bestaetigung-purge">
                  Zur Bestätigung den Namen eintippen: <strong>{m.name}</strong>
                </label>
                <input
                  id="cp-bestaetigung-purge"
                  value={bestaetigung}
                  onChange={(e) => setBestaetigung(e.target.value)}
                  autoComplete="off"
                />
                <div className="o-dialog-fuss">
                  <button className="o-btn o-btn-ghost" onClick={() => setForm(null)}>
                    Abbrechen
                  </button>
                  <button
                    className="o-btn o-btn-alert"
                    id="cp-btn-purge-bestaetigen"
                    disabled={
                      irgendwas || grund.trim().length < 3 || bestaetigung.trim() !== m.name.trim()
                    }
                    onClick={() => void purgeAusfuehren()}
                  >
                    {wartet('purge') ? 'Wird gelöscht …' : 'Endgültig löschen'}
                  </button>
                </div>
              </div>
            )}

            {form === 'sofort' && (
              <div className="cp-karte" id="cp-form-sofort">
                <p className="o-warn-text">
                  <strong>Das ist endgültig und auf ausdrücklichen Kundenwunsch.</strong> Alle Daten
                  dieses Betriebs werden entfernt: Kunden, Projekte, Rapporte, Fotos, Belege,
                  Zugänge und Dateien. Ein laufendes Abo wird bei Stripe sofort beendet.
                </p>
                {/*
                  Was NICHT passiert, steht genauso da wie was passiert.

                  «Keine Gutschrift» ist die Auskunft, nach der hinterher
                  gefragt wird – und der Gründerentscheid ist eindeutig: keine
                  Rückvergütung, auch nicht anteilig. Wer hier löscht, soll das
                  gelesen haben, bevor er es dem Kunden zusagt.
                */}
                <p className="o-soft small">
                  Keine Gutschrift, keine Rückvergütung – auch nicht anteilig für den laufenden
                  Monat. Eine bereits gestellte Rechnung bleibt als Forderung bestehen. Rechnungen
                  und Zahlungshistorie bleiben bei Stripe.
                </p>
                <p className="o-soft small">
                  Betroffen: {m.kunden} Kunden · {m.projekte} Projekte · {m.rapporte} Rapporte ·{' '}
                  {m.rechnungen + m.offerten} Belege · {groesse(m.speicher.belegtBytes)} Dateien
                </p>
                <label htmlFor="cp-grund-sofort">Grund</label>
                <input
                  id="cp-grund-sofort"
                  value={grund}
                  onChange={(e) => setGrund(e.target.value)}
                  placeholder="z. B. Kunde verlangt Löschung per Mail vom 3. März"
                  autoComplete="off"
                />
                <Grundhinweis wert={grund} />
                <label htmlFor="cp-bestaetigung-sofort">
                  Zur Bestätigung den Namen eintippen: <strong>{m.name}</strong>
                </label>
                <input
                  id="cp-bestaetigung-sofort"
                  value={bestaetigung}
                  onChange={(e) => setBestaetigung(e.target.value)}
                  autoComplete="off"
                />
                {/*
                  Die Checkbox ist leer, wenn das Formular aufgeht, und der
                  Knopf bleibt grau, solange sie es ist. Der Server prüft sie
                  ein zweites Mal – eine Oberfläche, die sie weglässt, kommt
                  nicht durch.
                */}
                <label className="cp-ankreuz" htmlFor="cp-folgen-sofort">
                  <input
                    type="checkbox"
                    id="cp-folgen-sofort"
                    checked={folgenVerstanden}
                    onChange={(e) => setFolgenVerstanden(e.target.checked)}
                  />
                  <span>
                    Ich habe verstanden: Die Daten sind danach unwiederbringlich weg, das Abo ist
                    beendet, und es gibt keine Gutschrift.
                  </span>
                </label>
                <div className="o-dialog-fuss">
                  <button className="o-btn o-btn-ghost" onClick={() => setForm(null)}>
                    Abbrechen
                  </button>
                  <button
                    className="o-btn o-btn-alert"
                    id="cp-btn-sofort-bestaetigen"
                    disabled={
                      irgendwas ||
                      !folgenVerstanden ||
                      grund.trim().length < 3 ||
                      bestaetigung.trim() !== m.name.trim()
                    }
                    onClick={() => void sofortLoeschen()}
                  >
                    {wartet('sofort') ? 'Wird gelöscht …' : 'Jetzt endgültig löschen'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>
      )}
    </>
  )
}

/** Warum der Knopf noch grau ist – ein abgeschalteter Knopf sagt es sonst nicht. */
function Grundhinweis({ wert }: { wert: string }): JSX.Element | null {
  if (wert.trim().length >= 3) return null
  return (
    <p className="o-soft small" data-grundhinweis>
      Der Knopf wird frei, sobald die Begründung mindestens drei Zeichen hat.
    </p>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   Pakete & Nutzung · Automatik · Protokoll · Sicherheit
   ═══════════════════════════════════════════════════════════════════════════ */

function PaketeUndNutzung({
  daten,
  liste
}: {
  daten: BetreiberUebersicht | null
  liste: BetreiberListe | null
}): JSX.Element {
  if (!daten) return <p className="o-soft">Wird geladen …</p>
  return (
    <>
      <section className="cp-abschnitt">
        <div className="cp-abschnitt-kopf">
          <h2>Pakete</h2>
          <p>Die Preisliste ist die eine Wahrheit – Grenzen und Preise kommen von dort.</p>
        </div>
        <div className="cp-tabelle">
          <table>
            <thead>
              <tr>
                <th>Paket</th>
                <th className="cp-num">Preis / Monat</th>
                <th className="cp-num">Benutzer</th>
                <th className="cp-num">Speicher</th>
                <th className="cp-num">Betriebe</th>
              </tr>
            </thead>
            <tbody>
              {PAKETE.map((p) => (
                <tr key={p.id}>
                  <td data-feld="Paket">{p.name}</td>
                  <td data-feld="Preis / Monat" className="cp-num">
                    {preisText(p.preisRp)} {STEUERHINWEIS}
                  </td>
                  <td data-feld="Benutzer" className="cp-num">
                    {p.benutzer}
                  </td>
                  <td data-feld="Speicher" className="cp-num">
                    {p.speicherGb} GB
                  </td>
                  <td data-feld="Betriebe" className="cp-num">
                    {daten.betriebe.nachPaket[p.id] ?? 0}
                  </td>
                </tr>
              ))}
              <tr>
                <td data-feld="Paket">Ohne Paketkennung</td>
                <td data-feld="Preis / Monat" className="cp-num">
                  —
                </td>
                <td data-feld="Benutzer" className="cp-num">
                  —
                </td>
                <td data-feld="Speicher" className="cp-num">
                  —
                </td>
                <td data-feld="Betriebe" className="cp-num">
                  {daten.betriebe.nachPaket['(ohne)'] ?? 0}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section className="cp-abschnitt">
        <div className="cp-abschnitt-kopf">
          <h2>Nutzung</h2>
          <p>
            {daten.speicher.vollstaendig
              ? `über alle ${daten.speicher.gemessen} Betriebe`
              : `über ${daten.speicher.gemessen} von ${daten.betriebe.gesamt} Betrieben`}
          </p>
        </div>
        <dl className="cp-kennzahlen">
          <Kennzahl
            titel="Speicher belegt"
            wert={groesse(daten.speicher.belegtBytes)}
            zusatz={`von ${groesse(daten.speicher.grenzeBytes)} · ${daten.speicher.prozent} %`}
          />
          <Kennzahl titel="Am Limit" wert={daten.speicher.voll} zusatz="100 % erreicht" />
          <Kennzahl titel="Über 80 %" wert={daten.speicher.ueberSchwelle} zusatz="Warnschwelle" />
          <Kennzahl
            titel="Benutzer gesamt"
            wert={daten.benutzer.gesamt}
            zusatz={liste ? `über ${liste.mandanten.length} Betriebe` : undefined}
          />
        </dl>
      </section>

      {/*
        Was bezahlt wird – neben dem, was gebucht ist.

        Hier stand ein fester Satz, es gebe keine Abrechnung. Das stimmte, bis
        Stripe angebunden wurde. Jetzt stehen die Bestände da, die `paket`
        gegenüberstellen: `plan_key` ist die Berechtigung, `tenant_abrechnung`
        der Beleg. Der Frankenbetrag steht in der Übersicht, wo er hingehört.
      */}
      <section className="cp-abschnitt">
        <div className="cp-abschnitt-kopf">
          <h2>Abrechnung</h2>
          <p>Bestände aus Stripe · Beträge stehen in der Übersicht</p>
        </div>
        <dl className="cp-kennzahlen">
          <Kennzahl titel="Zahlende Abos" wert={daten.abo.zahlendAktiv} zusatz="laufend" />
          <Kennzahl
            titel="Zahlung offen"
            wert={daten.abo.zahlungsproblem}
            zusatz="Abbuchung fehlgeschlagen"
            ton={daten.abo.zahlungsproblem > 0 ? 'alert' : undefined}
          />
          <Kennzahl
            titel="Ohne Abo"
            wert={daten.abo.ohneAbo}
            zusatz="keine Stripe-Zuordnung"
          />
          <Kennzahl
            titel="Testphase läuft"
            wert={daten.abo.testphaseLaufend}
            zusatz={`${daten.abo.testphaseAbgelaufen} abgelaufen`}
          />
        </dl>
      </section>
    </>
  )
}

function Automatik({ daten }: { daten: BetreiberUebersicht | null }): JSX.Element {
  if (!daten) return <p className="o-soft">Wird geladen …</p>
  const a = daten.automatik

  /*
    Die Jobzeilen: bekannte Jobs als Grundmenge, Datenbankstand obendrauf.

    ──────────────────────────────────────────────────────────────────────────
    Produktionsbefund, und es war mein Fehler
    ──────────────────────────────────────────────────────────────────────────

    `vertrag.automatik` fehlte in der Liste, obwohl er angemeldet ist. Der
    Grund lag nicht am Server: Die Anmeldung geschieht in `meldeGrundjobsAn()`
    beim Aufbau der Anwendung, und `automatik.angemeldet` trug den Job auch
    korrekt.

    Der Fehler stand hier. `angemeldet` wurde nur benutzt, WENN die Tabelle
    leer war – als Ersatztext für «noch gar nichts gelaufen». Vier Jobs waren
    inzwischen gelaufen, also wurde die Tabelle gezeichnet, und die Tabelle
    kannte nur `jobs` aus `plattform_jobs`. Dort bekommt ein Job seine Zeile
    aber erst beim ERSTEN Lauf. Ein frisch dazugekommener Job war damit
    unsichtbar, bis er einmal gelaufen war – und bei einem Wecker pro Tag
    heisst das: einen Tag lang.

    Die Anzeige geht jetzt von dem aus, was der Prozess KENNT, und legt den
    Datenbankstand darüber. Was noch nie gelaufen ist, steht mit genau diesem
    Wort da.
  */
  const staende = new Map(a.jobs.map((j) => [j.job, j]))
  const namen = [...new Set([...a.angemeldet, ...a.jobs.map((j) => j.job)])].sort()
  const zeilen = namen.map((name) => {
    const stand = staende.get(name)
    return {
      job: name,
      gelaufen: stand !== undefined,
      laeuft: stand?.laeuft ?? false,
      aktiv: stand?.aktiv ?? true,
      letztesEnde: stand?.letztesEnde ?? null,
      letztesErgebnis: stand?.letztesErgebnis ?? null,
      letzterFehler: stand?.letzterFehler ?? null
    }
  })

  return (
    <>
      <section className="cp-abschnitt">
        <div className="cp-abschnitt-kopf">
          <h2>Zustand</h2>
          <p>Was heute läuft und was bewusst noch nicht.</p>
        </div>
        <dl className="cp-kennzahlen">
          <Kennzahl titel="Zeitplaner" wert={a.zeitplanAktiv ? 'läuft' : 'aus'} leer={!a.zeitplanAktiv} />
          <Kennzahl
            titel="Mailversand"
            wert={a.versandAktiv ? 'läuft' : 'aus'}
            leer={!a.versandAktiv}
            zusatz={a.versandAktiv ? undefined : 'Nachrichten entstehen, keine geht hinaus'}
          />
          <Kennzahl
            titel="Speicherlimit"
            wert={a.speicherlimitAktiv ? 'greift' : 'aus'}
            leer={!a.speicherlimitAktiv}
            zusatz={a.speicherlimitAktiv ? undefined : 'gilt für Beleg-PDF und Logo'}
          />
          <Kennzahl
            titel="Benutzerlimit"
            wert={a.benutzergrenzeAktiv ? 'greift' : 'aus'}
            leer={!a.benutzergrenzeAktiv}
          />
          {/*
            Ob der Betrieb sein Abo selbst ändern kann.

            Gehört auf diese Seite, weil sie die Frage beantwortet «was läuft
            ohne mich». Steht der Schalter auf aus, ist jeder Paketwechsel
            wieder ein Anruf – und das soll man hier sehen und nicht im
            Quelltext nachschlagen.
          */}
          <Kennzahl
            titel="Abo-Selbstbedienung"
            wert={a.aboSelbstbedienung ? 'läuft' : 'aus'}
            leer={!a.aboSelbstbedienung}
            zusatz={
              a.aboSelbstbedienung
                ? 'Betriebe wechseln ihr Paket selbst'
                : 'jeder Wechsel läuft über dich'
            }
          />
        </dl>
      </section>

      <section className="cp-abschnitt">
        <div className="cp-abschnitt-kopf">
          <h2>Jobs</h2>
        </div>
        {/*
          «Kein Job angemeldet» war die falsche Auskunft.

          `plattform_jobs` bekommt eine Zeile erst, wenn ein Job zum ersten Mal
          gelaufen ist. Solange der Zeitplaner aus ist, läuft keiner, die
          Tabelle bleibt leer – und der Satz schickte den Leser auf die Suche
          nach einer fehlenden Anmeldung, während in Wahrheit nur der Schalter
          aus war. Jetzt steht da, was der Prozess kennt und dass es noch nie
          gelaufen ist.
        */}
        {zeilen.length === 0 ? (
          <p className="cp-ruhig" id="cp-jobs-leer">
            Kein Job angemeldet.
          </p>
        ) : (
          <div className="cp-tabelle">
            <table>
              <thead>
                <tr>
                  <th>Job</th>
                  <th>Zustand</th>
                  <th>Letztes Ende</th>
                  <th>Ergebnis</th>
                </tr>
              </thead>
              <tbody>
                {zeilen.map((j) => (
                  <tr key={j.job} data-job={j.job}>
                    <td data-feld="Job">{j.job}</td>
                    <td data-feld="Zustand">
                      {!j.gelaufen ? (
                        <span className="o-badge o-badge-neutral">noch nie gelaufen</span>
                      ) : j.laeuft ? (
                        <span className="o-badge o-badge-neutral">läuft</span>
                      ) : j.aktiv ? (
                        <span className="o-badge o-badge-ok">bereit</span>
                      ) : (
                        <span className="o-badge o-badge-neutral">aus</span>
                      )}
                    </td>
                    <td data-feld="Letztes Ende">{j.letztesEnde ?? '—'}</td>
                    <td data-feld="Ergebnis">
                      {j.letztesErgebnis === 'fehler' ? (
                        <span className="o-badge o-badge-warn">{j.letzterFehler ?? 'Fehler'}</span>
                      ) : (
                        (j.letztesErgebnis ?? '—')
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="cp-abschnitt">
        <div className="cp-abschnitt-kopf">
          <h2>Benachrichtigungen</h2>
        </div>
        {Object.keys(daten.nachrichten).length === 0 ? (
          <p className="cp-ruhig">Noch keine Nachricht angelegt.</p>
        ) : (
          <dl className="cp-kennzahlen">
            {Object.entries(daten.nachrichten).map(([status, n]) => (
              <Kennzahl key={status} titel={status} wert={n} />
            ))}
          </dl>
        )}
      </section>
    </>
  )
}

function Protokoll(): JSX.Element {
  const [daten, setDaten] = useState<BetreiberProtokoll | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  useEffect(() => {
    api
      .betreiberProtokoll(100)
      .then(setDaten)
      .catch((e: unknown) => setFehler(e instanceof Error ? e.message : 'Unbekannter Fehler'))
  }, [])

  if (fehler) return <p className="o-warn-text">{fehler}</p>
  if (!daten) return <p className="o-soft">Wird geladen …</p>

  return (
    <section className="cp-abschnitt">
      <div className="cp-abschnitt-kopf">
        <h2>Betreiberprotokoll</h2>
        <p>Jeder Zugriff und jede Handlung an einem fremden Betrieb.</p>
      </div>
      <div className="cp-tabelle" id="cp-protokoll">
        <table>
          <thead>
            <tr>
              <th>Wann</th>
              <th>Betrieb</th>
              <th>Handlung</th>
              <th>Betreiber</th>
              <th>Herkunft</th>
            </tr>
          </thead>
          <tbody>
            {daten.eintraege.map((e, i) => (
              <tr key={i}>
                <td data-feld="Wann">{e.wann}</td>
                <td data-feld="Betrieb">{e.betrieb}</td>
                <td data-feld="Handlung">{e.aktion}</td>
                <td data-feld="Betreiber">{e.betreiber}</td>
                <td data-feld="Herkunft" className="cp-code">
                  {oder((e as { herkunft?: string | null }).herkunft)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function Sicherheit({ daten }: { daten: BetreiberUebersicht | null }): JSX.Element {
  return (
    <>
      <section className="cp-abschnitt">
        <div className="cp-abschnitt-kopf">
          <h2>Was diesen Bereich schützt</h2>
        </div>
        <div className="cp-karte">
          <ul className="o-liste">
            <li>
              Jede eingreifende Handlung verlangt einen frischen Sicherheitsnachweis über den
              zweiten Faktor des eigenen Kontos.
            </li>
            <li>
              Testbetrieb markieren und endgültig löschen verlangen zusätzlich die Plattformrolle
              <strong> OWNER</strong>.
            </li>
            <li>
              Endgültig gelöscht wird nur ein Betrieb, der als Testbetrieb markiert ist, dessen
              Name auf das Zeichen genau eingetippt wurde – ein Betrieb je Vorgang.
            </li>
            <li>Der Plattformbetrieb ist von Sperre, Vormerkung und Löschung ausgeschlossen.</li>
            <li>
              Geschäftsdaten der Betriebe sind hier nicht einsehbar. Sichtbar sind Stammdaten,
              Zahlen und Zustände.
            </li>
            <li>Jeder Zugriff auf ein Betriebsdetail steht im Protokoll.</li>
          </ul>
        </div>
      </section>

      {daten && (
        <section className="cp-abschnitt">
          <div className="cp-abschnitt-kopf">
            <h2>Aktuell scharf geschaltet</h2>
          </div>
          <dl className="cp-kennzahlen">
            <Kennzahl
              titel="Zeitplaner"
              wert={daten.automatik.zeitplanAktiv ? 'ein' : 'aus'}
              leer={!daten.automatik.zeitplanAktiv}
            />
            <Kennzahl
              titel="Mailversand"
              wert={daten.automatik.versandAktiv ? 'ein' : 'aus'}
              leer={!daten.automatik.versandAktiv}
            />
            <Kennzahl
              titel="Speicherlimit"
              wert={daten.automatik.speicherlimitAktiv ? 'ein' : 'aus'}
              leer={!daten.automatik.speicherlimitAktiv}
            />
            <Kennzahl
              titel="Benutzerlimit"
              wert={daten.automatik.benutzergrenzeAktiv ? 'ein' : 'aus'}
              leer={!daten.automatik.benutzergrenzeAktiv}
            />
          </dl>
        </section>
      )}
    </>
  )
}
